// Boost Product Selection page — resolves context server-side via getBoostContext
// so that offerToken never reaches the client.

import { useProductSelectionState } from "@/lib/hooks/use-product-selection";
import type { MemberDataResponse } from "@/lib/queries/member-data";
import { useBoostContextQuery } from "@/lib/queries/products";
import { useProfileQuery } from "@/lib/queries/profile";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import { z } from "zod";
import type { CampaignRouteContext } from "@/lib/types/route-context";
import { ProductSelectionPageView } from "./product-selection.$interactionId";
import type { OrderRequest } from "@/lib/hooks/use-product-selection-types";

const boostProductSelectionSearchSchema = z.object({
  pfaOrderId: z.string(),
  returnTo: z.string().optional(),
});

export const Route = createFileRoute(
  "/(org)/o/$orgSlug/p/$profileSlug/c/$campaignId/boost-product-selection/$interactionId",
)({
  component: BoostProductSelectionContainer,
  validateSearch: boostProductSelectionSearchSchema,
});

function BoostProductSelectionContainer() {
  const { interactionId, orgSlug } = Route.useParams();
  const { pfaOrderId, returnTo } = Route.useSearch();
  const context = Route.useRouteContext() as CampaignRouteContext;
  const { campaign, programId, profile, organization } = context;
  const organizationId = organization.id;
  const navigate = useNavigate();

  // Single server-side call resolves orderKey + orderRequest + memberData
  const { data: boostContext, isPending: isContextPending } =
    useBoostContextQuery(organizationId, programId, profile.id, pfaOrderId);
  const { data: profileData } = useProfileQuery(organizationId, profile.id);
  const orderKey = boostContext?.orderKey ?? "";

  // Cart persistence key scoped to this specific boost order
  const cartStorageKey = `cart-boost-${organizationId}-${pfaOrderId}`;

  // Validate orderRequest shape from the API response
  const orderRequest = useMemo((): OrderRequest | null => {
    const raw = boostContext?.orderRequest;
    if (typeof raw !== "object" || raw === null) return null;
    if (!("id" in raw) || typeof raw.id !== "string") return null;
    if (!("type" in raw) || typeof raw.type !== "string") return null;
    return raw as OrderRequest;
  }, [boostContext?.orderRequest]);

  // Post-submit navigation callback
  const handleSubmitSuccess = useCallback(() => {
    const isSafeReturn =
      returnTo &&
      returnTo.startsWith("/") &&
      !returnTo.startsWith("//") &&
      !/^\/[a-z]+:/i.test(returnTo);
    if (isSafeReturn) {
      void navigate({ to: returnTo });
    } else {
      void navigate({
        to: "/o/$orgSlug/p/$profileSlug/c/$campaignId",
        params: {
          orgSlug,
          profileSlug: campaign.profileSlug,
          campaignId: String(programId),
        },
      });
    }
  }, [navigate, returnTo, orgSlug, campaign.profileSlug, programId]);

  // Narrow memberData: only pass if it has the expected shape
  const memberData = useMemo((): MemberDataResponse | undefined => {
    const md = boostContext?.memberData;
    if (!md || typeof md !== "object") return undefined;
    if (!("member" in md) || !("fields" in md)) return undefined;
    return md as MemberDataResponse;
  }, [boostContext?.memberData]);

  // Shared product selection state
  const hookResult = useProductSelectionState({
    organizationId,
    orderKey,
    programId,
    cartStorageKey,
    orderRequest,
    isOrderPending: isContextPending,
    clientId: orderRequest?.clientId ?? "",
    isBoost: true,
    pfaOrderId,
    memberData,
    profileAddresses: profileData?.addresses,
    profileEmail: profileData?.email,
    profileId: profile.id,
    onSubmitSuccess: handleSubmitSuccess,
    analyticsPrefix: "boost_product",
    analyticsExtraProps: { pfaOrderId },
    supportsNoCatalogue: false,
  });

  // Keep skeleton visible until products actually arrive to prevent
  // a flash of "No products found" during the initial fetch (EPD-6052).
  const isLoading =
    hookResult.isLoading ||
    (hookResult.isFetching && hookResult.products.length === 0);

  return (
    <ProductSelectionPageView
      key={`boost-${organizationId}-${pfaOrderId}`}
      interactionId={interactionId}
      {...hookResult}
      isLoading={isLoading}
    />
  );
}
